import { Directive, EventEmitter, HostListener, Input, Output } from '@angular/core';

/**
 * Infinite Scroll Directive
 *
 * Detects when the user has scrolled near the bottom of the page and emits an event.
 * Can be used to implement infinite scroll pagination on any component.
 *
 * Usage:
 * ```html
 * <div appInfiniteScroll
 *      [scrollThreshold]="200"
 *      [isLoading]="isLoading"
 *      [hasMore]="hasMorePages"
 *      (scrolled)="loadNextPage()">
 *   <!-- Your scrollable content -->
 * </div>
 * ```
 *
 * @Input scrollThreshold - Distance from bottom (in pixels) to trigger load. Default: 200
 * @Input isLoading - Prevents triggering while loading. Default: false
 * @Input hasMore - Prevents triggering when no more data. Default: true
 * @Output scrolled - Emits when scroll threshold is reached
 */
@Directive({
  selector: '[appInfiniteScroll]',
  standalone: true
})
export class InfiniteScrollDirective {
  /**
   * Distance from bottom (in pixels) to trigger the load event
   */
  @Input() scrollThreshold = 200;

  /**
   * Prevents triggering the load event while data is being loaded
   */
  @Input() isLoading = false;

  /**
   * Prevents triggering the load event when there's no more data
   */
  @Input() hasMore = true;

  /**
   * Emits when the user has scrolled near the bottom
   */
  @Output() scrolled = new EventEmitter<void>();

  /**
   * Listen to window scroll events
   */
  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    // Don't trigger if already loading or no more data
    if (this.isLoading || !this.hasMore) {
      return;
    }

    // Calculate scroll position
    const scrollPosition = window.pageYOffset + window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Check if user has scrolled to threshold
    if (scrollPosition >= documentHeight - this.scrollThreshold) {
      this.scrolled.emit();
    }
  }
}
